import { useState } from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { format } from 'date-fns';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { addEvent } from '../services/firestoreService';
const emptyForm = { title: '', date: '', time: '', location: '', description: '' };

// The association's usual actions, one tap to fill the title
const titleSuggestions = ['Maraude', 'Distribution', 'Collecte alimentaire', 'Événement solidaire'];
const LOCATION_TBD = 'Lieu à définir';

// Phone-first page (PWA start URL): fill in a few fields and publish.
const QuickAddEvent = () => {
  const [form, setForm] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [published, setPublished] = useState<typeof emptyForm | null>(null);

  const today = format(new Date(), 'yyyy-MM-dd');

  const updateField = (field: keyof typeof emptyForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const title = form.title.trim();
    const location = form.location.trim();

    if (title.length < 3) return setError('Le titre doit contenir au moins 3 caractères.');
    if (!form.date || form.date < today) return setError("La date doit être aujourd'hui ou dans le futur.");
    if (!form.time) return setError("Indiquez l'heure.");
    if (location.length < 3) return setError('Le lieu doit contenir au moins 3 caractères.');

    setIsSubmitting(true);
    setError(null);
    try {
      const eventData = {
        title: DOMPurify.sanitize(title),
        date: form.date,
        time: form.time,
        location: DOMPurify.sanitize(location),
        description: DOMPurify.sanitize(form.description.trim()),
      };
      await addEvent(eventData);
      setPublished(eventData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Publication impossible, réessayez.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const reset = () => {
    setForm(emptyForm);
    setPublished(null);
    setError(null);
  };

  if (published) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-4">
        <div className="panel w-full max-w-md p-8 text-center">
          <CheckCircleIcon className="h-16 w-16 mx-auto text-accent" />
          <h1 className="heading text-3xl mt-4 mb-2">Publié !</h1>
          <p className="text-brand-pink-900/80 mb-1 font-semibold">{published.title}</p>
          <p className="text-brand-pink-900/70 mb-8">
            {published.date} · {published.time} · {published.location}
          </p>
          <div className="flex flex-col gap-3">
            <button type="button" onClick={reset} className="btn-primary w-full text-lg">
              Ajouter un autre événement
            </button>
            <a href="/#calendrier" className="btn-secondary w-full">Voir sur le site</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper">
      <header className="sticky top-0 z-10 bg-paper/90 backdrop-blur-md border-b border-ink/10 px-4 py-3 flex items-center justify-between">
        <span className="heading text-lg text-accent">Nouvel événement</span>
        <Link to="/admin" className="text-sm font-semibold text-ink/70 hover:text-ink">Admin</Link>
      </header>

      <main className="max-w-lg mx-auto p-4 pb-16">
        <form onSubmit={handleSubmit} className="card p-5 mt-4 space-y-5">
          <div>
            <label htmlFor="event-title" className="block font-semibold text-ink mb-1">Titre</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {titleSuggestions.map(suggestion => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => updateField('title', suggestion)}
                  className={form.title === suggestion ? 'chip-on' : 'chip-off'}
                  aria-pressed={form.title === suggestion}
                >
                  {suggestion}
                </button>
              ))}
            </div>
            <input id="event-title" value={form.title} onChange={e => updateField('title', e.target.value)} className="input text-lg" placeholder="Ou un autre titre…" required />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block font-semibold text-ink mb-1">Date</span>
              <input type="date" min={today} value={form.date} onChange={e => updateField('date', e.target.value)} className="input text-lg" required />
            </label>
            <label className="block">
              <span className="block font-semibold text-ink mb-1">Heure</span>
              <input type="time" value={form.time} onChange={e => updateField('time', e.target.value)} className="input text-lg" required />
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="event-location" className="font-semibold text-ink">Lieu</label>
              <button
                type="button"
                onClick={() => updateField('location', LOCATION_TBD)}
                className={form.location === LOCATION_TBD ? 'chip-on py-1' : 'chip-off py-1'}
                aria-pressed={form.location === LOCATION_TBD}
              >
                À définir
              </button>
            </div>
            <input id="event-location" value={form.location} onChange={e => updateField('location', e.target.value)} className="input text-lg" placeholder="Ex : Gare de Lyon" required />
          </div>

          <label className="block">
            <span className="block font-semibold text-ink mb-1">Détails <span className="font-normal text-ink/50">(optionnel)</span></span>
            <textarea value={form.description} onChange={e => updateField('description', e.target.value)} rows={3} className="input" />
          </label>

          {error && <p role="alert" className="text-sm font-semibold text-red-600">{error}</p>}

          <button type="submit" disabled={isSubmitting} className="btn-primary w-full text-lg py-3.5 disabled:opacity-60">
            {isSubmitting ? 'Publication...' : 'Publier'}
          </button>
        </form>
      </main>
    </div>
  );
};

export default QuickAddEvent;
